<template>
    <div class="w-full">
        <div 
            class="w-full flex items-center"
            :class="showReorderButton ? 'justify-between' : 'justify-end'"
        >
            <div class="flex items-center" v-if="showReorderButton">
                <div>
                    <el-select
                        class="w-48 per-page-select"
                        size="large"
                        v-model="perPage"
                    >
                        <el-option :key="option" :value="parseInt(option)" :label="`${option} ${$t('per page')}`" v-for="option in pageSizes"/>
                    </el-select>
                </div>
                <el-input
                    clearable
                    :placeholder="$t('general.search')"
                    size="large"
                    v-model="filter">
                    <i slot="prefix" class="el-input__icon vc-icon-search icon-md text-primary ml-1" />
                </el-input>
            </div>
            <div class="flex items-center h-7"> 
                <base-button
                    v-if="showReorderButton && !checkIfWidgetsHaveExport"
                    type="primary"
                    size="xs"
                    class="mx-4"
                    link
                    @click="changeWidgetsModal"
                >
                    <i class="vc-icon-arrow-up icon-md"/>
                    <i class="vc-icon-arrow-down icon-md"/>
                    {{ $t('report.changeWidgetsOrder') }}
                </base-button>
                <base-button
                    v-if="widgetsToAdd.length && this.reportItemList.length < LimitOfAddingWidgets"
                    :loading="loading"
                    type="primary"
                    size="xs"
                    link
                    @click="addWidgetModal"
                >
                    <span v-if="loading">
                        {{ $t('chart.loading') }}
                    </span>
                    <span v-else class="flex items-center">
                        <i class="vc-icon-add icon-md mx-2"/>
                        {{ $t('widget.addNewWidgets') }}
                    </span>
                </base-button>
            </div>
        </div>
        <div class="reports-table__wrapper mt-4">
            <el-table
                :data="paginatedData"
                :sortable="sortable"
                @sort-change="onSortChange"
                :height="showReorderButton ? 393 : 394"
                style="width: 100%;"
                size="mini"
            >
                <template slot="empty">
                    <div>
                        <div class="no-data uppercase main-text-color">
                            {{ $t('general.noData') }}
                        </div>
                        <div class="flex items-center justify-center">
                            <base-button
                                :loading="loading"
                                :disabled="!widgetsToAdd.length"
                                type="primary"
                                size="xs"
                                link
                                @click="addWidgetModal"
                            >
                                <span v-if="loading" class="add-new-widget-button">
                                    {{ $t('chart.loading') }}
                                </span>
                                <span v-else class="flex items-center add-new-widget-button">
                                    {{ $t('widget.addNewWidgets') }}
                                </span>
                            </base-button>
                        </div>
                        <div class="flex items-center justify-center">
                            <img :src="getLogo"
                                alt="Logo"
                                class="opacity-10 max-h-40 object-cover"
                            >
                        </div>
                    </div>
                </template>

                <el-table-column
                    prop="index"
                    width="80"
                    sortable
                >
                    <template slot="header">
                        <div class="text-primary flex items-center">
                            {{ $t('general.number.symbol') }}
                        </div>
                    </template>
                    <template slot-scope="{row}">
                        <div
                            class="text-center"
                        >
                            <slot
                                :name="['index']"
                                :row="row"
                            >
                                {{ row['index'] }}
                            </slot>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column
                    v-for="(column, index) in tableProps"
                    :key="`${column.label}-${index}`"
                    :prop="column.prop"
                    :min-width="column.minWidth"
                    sortable
                >
                    <template slot="header">
                        <div class="text-primary flex items-center">
                            <i v-if="column.icon" :class="column.icon" class="icon-md action-icon" />
                            {{ $t(column.label) }}
                        </div>
                    </template>
                    <template slot-scope="{row}">
                        <div class="flex items-center justify-center">
                            <span class="px-2 text-xs">
                                <slot
                                    :name="[column.prop]"
                                    :row="row"
                                >
                                    {{ row[column.prop] }}
                                </slot>
                            </span>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column
                    v-for="(column, index) in dynamicActionColumn"
                    :key="`${column.label}-${index}`"
                    :width="column.width"
                >
                    <template slot="header">
                        <div class="text-primary flex items-center">
                            <i v-if="column.icon" :class="[column.icon, column.prop === 'emailbody' ? 'text-xl' : 'text-lg' ]" class="action-icon" />
                            <span>
                                {{ $t(column.label) }}
                            </span>
                        </div>
                    </template>
                    <template slot-scope="{row}">
                        <div class="text-center">
                            <div class="py-4 flex items-center justify-center" v-if="column.prop !== 'action'">
                                <el-checkbox
                                    v-if="reloadSelect"
                                    :disabled="disablingCheckbox(column, row)"
                                    @change="onChange(column, row)"
                                    :checked="!!(row.ReportItemExport.length && row.ReportItemExport.some(el => el.ReportExportTypeID === column.ReportExportTypeID))"
                                />
                            </div>
                            <div v-else>
                                <slot :name="[column.prop]" :row="row"/>
                            </div>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </div>
        <reports-pagination
            :currentPage="currentPage"
            :totalPages="totalPages"
            :paginationFrom="paginationFrom"
            :paginationTo="paginationTo"
            :paginationTotal="paginationTotal"
            @go-to-prev-page="goToPrevPage"
            @go-to-next-page="goToNextPage"
            @on-page-select="onPageSelect"/>
        <add-widgets-dialog
            :visible.sync="visible"
            @added-widgets="addedWidgets"
            :do-request="false"
            :reportId="reportId"
            :widgets="widgetsToAdd"
            :widgetsBefore="tableData.length"
        />
        <reorder-widgets-dialog
            :visible.sync="visibleReorderModal"
            :widgets="tableData"
            :reportId="reportId"
            :reportItemList="reportItemList"
        />
    </div>
</template>

<script>
import { Table, TableColumn, Tooltip, Checkbox, Select, Option } from 'element-ui'
import Fuse from 'fuse.js';

import BaseButton from "@/components/Common/Buttons/BaseButton";
import ButtonIcon from "@/modules/common/components/ButtonIcon";
import ReportsPagination from "@/modules/reports/components/ReportsPagination";
import cloneDeep from "lodash/cloneDeep";
import AddWidgetsDialog from '@/components/Reports/Widgets/AddWidgetsDialog.vue'
import { REPORT_ITEM_STATUS_ID_ENABLED, ReportExportXLSX, ReportExportTypeIDs } from '@/enum/reportStatuses'
import { reportApi } from '@/modules/reports/services/reportService'
import { Notification } from 'element-ui'
import { WidgetApi } from '@/api/widgetApi'
const LimitOfAddingWidgets = 50

export default {
    name: "reports-table",
    props: {
        showReorderButton: {
            type: Boolean,
            default: false
        },
        reportId: {
            type: [Number, String],
            default: ''
        },
        reportItemList: {
            type: Array,
            default: () => []
        },
        edit: {
            type: Boolean,
            default: false
        }
    },
    components: {
        BaseButton,
        [Table.name]: Table,
        [Tooltip.name]: Tooltip,
        [TableColumn.name]: TableColumn,
        ButtonIcon,
        ReportsPagination,
        AddWidgetsDialog,
        [Checkbox.name]: Checkbox,
        [Select.name]: Select,
        [Option.name]: Option,
        ReorderWidgetsDialog: () => import('@/modules/reports/components/widget-reorder/ReorderWidgetsModal.vue')
    },
    data () {
        return {
            filter: '',
            searchData: [],
            currentPage: 1,
            perPage: 5,
            fuseSearch: null,
            defaultSort: { prop: 'index', order: 'ascending' },
            visible: false,
            tableProps: [
                {
                    prop: 'DashboardTitle',
                    label: 'dashboard.dashboardName',
                    icon: 'vc-icon-name',
                    minWidth: '150'
                },
                {
                    prop: 'WidgetGroupTitle',
                    label: 'widget.widgetGroupName',
                    icon: 'vc-icon-name',
                    minWidth: '150'
                },
                {
                    prop: 'WidgetTitle',
                    label: 'widget.widgetName',
                    icon: 'vc-icon-name',
                    minWidth: this.edit ? '240' : '200'
                }
            ],
            sortable: false,
            showStatusText: false,
            pageSizes: [5, 10, 25, 50, 100, 500],
            customPageSize: 10,
            visibleReorderModal: false,
            reloadSelect: true,
            widgets: [],
            widgetsToAdd: [],
            loading: true,
            LimitOfAddingWidgets
        }
    },
    async mounted () {
        this.widgets = await this.getAllWidgets()
    },
    methods: {
        onPageSelect(page) {
            this.currentPage = page
            this.reInitCheckboxes()
        },
        initFuseSearch() {

            const fuseProps = this.tableProps.map(column => {
                return column.prop === 'ReportItemList' ?
                    column.prop + '.WidgetTitle':
                    column.prop === 'ReportTriggerList' ?
                        column.prop + '.ReportTriggerName':
                        column.prop
            })

            this.fuseSearch = new Fuse(this.tableData, {
                keys: fuseProps,
                threshold: 0.3
            });
            if (this.defaultSort) {
                this.$nextTick(() => {
                    this.onSortChange(this.defaultSort)
                })
            }
        },
        onSortChange({column, prop, order}) {
            if (prop === null) {
                return
            }

            if (order === 'ascending') {
                this.searchData.sort((a, b) => (a[prop] > b[prop]) ? 1 : -1)
            } else if (order === 'descending') {
                this.searchData.sort((a, b) => (a[prop] < b[prop]) ? 1 : -1)
            }
        },
        goToPrevPage() {
            if (this.currentPage > 1) {
                this.currentPage--
            }
            this.reInitCheckboxes()
        },
        goToNextPage() {
            if (this.currentPage < this.totalPages.length) {
                this.currentPage++
            }
            this.reInitCheckboxes()
        },
        addWidgetModal (data) {
            this.visible = true
        },
        addedWidgets ({ activeWidgetItems }) {
            activeWidgetItems.forEach(el => {
                const currentWidget = this.widgets.find(widget => widget.WidgetID === el.WidgetID)
                el.WidgetTitle = this.translateTemplateNameContentTag(currentWidget)
                el.WidgetTemplateID = currentWidget.TemplateID
                el.ReportItemExport = []
                el.ReportItemStatusID = REPORT_ITEM_STATUS_ID_ENABLED
                this.$emit('updateReportItemList', el)
            })
        },
        disablingCheckbox (column, row) {
            const widgetWithWidgetTemplateList = this.findTheSameWidget(row.WidgetTemplateID)
            if (!widgetWithWidgetTemplateList || !widgetWithWidgetTemplateList.ReportExportData) {
                return true
            }

            return !Object.keys(widgetWithWidgetTemplateList.ReportExportData).some(el => column.ReportExportTypeName === el)
        },
        onChange (column, row) {
            if (!row.ReportItemExport.length || !row.ReportItemExport.find(el => Number(el.ReportExportTypeID) === Number(column.ReportExportTypeID))) {
                const data = {
                    ReportExportOrder: 0,
                    ReportExportTypeID: column.ReportExportTypeID,
                    ReportExportTypeName: '',
                    ReportItemID: row.ReportItemID
                }

                this.reportItemList[row.index - 1].ReportItemExport.push(data)

                row.ReportItemExport.push(
                    data
                )
            } else {
                const indexColumn = row.ReportItemExport.findIndex(el => el.ReportExportTypeID === column.ReportExportTypeID)

                this.reportItemList[row.index - 1].ReportItemExport.splice(indexColumn, 1)

                row.ReportItemExport.splice(indexColumn, 1)
            }
            
            if (this.edit) {
                const copiedRowData = cloneDeep(row)

                delete copiedRowData.index
                delete copiedRowData.WidgetTitle
                delete copiedRowData.ReportItemData
                delete copiedRowData.ReportItemStatusName
                const data = {
                    ReportItemList: [copiedRowData]
                }

                reportApi.reportItemBulkUpsert(data)
                Notification.success(this.$t('general.notification.success'))
            }
        },
        changeWidgetsModal () {
            this.visibleReorderModal = true
        },
        reInitCheckboxes () {
            this.reloadSelect = false
            this.$nextTick(() => {
                this.reloadSelect = true
            })
        },
        async getAllWidgets () {
            let allWidgets = []
            const selectedAccountId = this.$store.state.entities.selectedAccountID
            const dashboards = await WidgetApi.getAllWidgetsByAccountId(selectedAccountId)

            if (!dashboards) {
                return allWidgets
            }

            allWidgets = Object.values(dashboards).map(dashboard => {
                dashboard.isChecked = false
                return dashboard
            })

            allWidgets = allWidgets.filter(el => {
                if (!this.findTheSameWidget(el.TemplateID)) {
                    return false
                }

                const reportExportData = this.findTheSameWidget(el.TemplateID).ReportExportData
                if (!reportExportData) {
                    return false
                }
                const hasAtLeastOneExportType = () => {
                    return Object.values(reportExportData).some(el=> ReportExportTypeIDs.indexOf(el.ReportExportTypeID) !== -1)
                }
                return hasAtLeastOneExportType()
            })

            this.loading = false

            return allWidgets
        },
        translateTemplateNameContentTag(widget) {
            return this.$te(widget.TemplateNameContentTag) ? this.$t(widget.TemplateNameContentTag) : widget.WidgetTitle
        },
        findTheSameWidget (templateID) {
            return this.widgetTemplateList.find(el => el.WidgetTemplateID === templateID)
        }
    },
    computed: {
        tableData () {
            return this.reportItemList
        },
        paginatedData() {
            if (this.searchData.length < this.perPage) {
                return this.searchData
            }

            const f = this.perPage * this.currentPage
            return this.searchData.slice(f - this.perPage, f)
        },
        totalPages() {
            const length = this.searchData.length / this.perPage
            const fullLength = this.searchData.length % this.perPage?
                length  + 1:
                length

            let pages = []
            for (let i = 1; i <= fullLength; i++) {
                pages.push(i)
            }
            return pages
        },
        paginationFrom() {
            if (!this.searchData.length) return 0

            return this.currentPage * this.perPage - this.perPage + 1
        },
        paginationTo() {
            if (!this.searchData.length) return 0

            if (this.totalPages.length === this.currentPage) {
                const remainder = this.searchData.length % this.perPage
                if (remainder) {
                    return this.currentPage * this.perPage - this.perPage + remainder
                }
            }
            return this.currentPage * this.perPage
        },
        paginationTotal() {
            return this.searchData.length
        },
        confData () {
            return this.$store.getters['reportTrigger/getConfData']
        },
        widgetTemplateList () {
            return this.confData.WidgetTemplateList
        },
        dynamicActionColumn () {
            const dynamicColumns = this.confData.ReportExportTypeList
                .map(el => {
                    el.prop = String(el.ReportExportTypeName).toLowerCase().replaceAll(' ', '_')
                    el.label = this.$t(el.ReportExportTypeNameContentTag)
                    el.icon = ReportExportXLSX === el.ReportExportTypeID ? 'vc-icon-excel' : 'vc-icon-mail'
                    el.width = ReportExportXLSX === el.ReportExportTypeID ? '70' : '110'

                    return el
                })

            const exportTypeList = [
                    ...dynamicColumns,
                    {
                        prop: 'action',
                        label: 'general.action',
                        icon: 'vc-icon-recycle-bin',
                        width: '80'
                    }
            ]
            return exportTypeList
        },
        checkIfWidgetsHaveExport () {
            return !this.tableData.some(el => el.ReportItemExport.length)
        },
        widgetsIds () {
            return this.tableData.map(el => el.WidgetID)
        },
        getLogo() {
            return this.$store.getters['layout/getLogo']('activeLayout')
        }
    },
    watch: {
        tableData: {
            handler(newV) {
                this.initFuseSearch()
                const newData = newV.map((el, index) => {
                    el.index = index + 1
                    return el
                })
                this.searchData = cloneDeep(newData)
                this.reInitCheckboxes()
                this.widgetsToAdd = this.widgets.filter(el => !this.widgetsIds.includes(el.WidgetID))
            },
            deep: true,
            immediate: true
        },
        filter(newV) {
            let result

            this.currentPage = 1
            if (newV !== '') {
                result = this.fuseSearch.search(newV).map(el => el.item);
            } else {
                result = this.tableData
            }

            this.searchData = result;
        },
        widgets (val) {
            if (val) {
                this.widgetsToAdd = this.widgets.filter(el => !this.widgetsIds.includes(el.WidgetID))
            }
        },
        perPage () {
            this.currentPage = 1
        }
    },
}
</script>

<style scoped lang="scss">
.reports-table__wrapper ::v-deep .el-table {
    border: 1px solid var(--gray-300);
    @apply rounded-t-1xl border-b-0;

    .el-table__expand-column {
        .el-table__expand-icon {
            @apply text-primary;
        }
    }
}

[dir="ltr"] .reports-table__wrapper ::v-deep .el-table {
    .el-table__cell:not(:last-child):not(.el-table__expand-column) {
        border-right: 1px solid var(--gray-300);
    }
}
[dir="rtl"] .reports-table__wrapper ::v-deep .el-table {
    .el-table__cell:not(:first-child):not(.el-table__expand-column) {
        border-right: 1px solid var(--gray-300);
    }
}
[dir="ltr"] .per-page-select {
    @apply mr-4;
}
[dir="rtl"] .per-page-select {
    @apply ml-4;
}
[dir="ltr"] .action-icon {
    @apply mr-1;
}
[dir="rtl"] .action-icon {
    @apply ml-1;
}
::v-deep .el-table .el-table__cell {
    padding: 6px 0px !important;
}
.no-data {
    font-weight: 700;
    font-size: 40px;
    line-height: 49px;
    color: var(--gray-700);
    margin-bottom: 18px;
}
.add-new-widget-button {
    font-weight: 400;
    font-size: 20px;
    line-height: 24px;
    color: var(--primary-color);
    text-decoration: underline;
}

::v-deep .el-table th.el-table__cell>.cell {
    display: flex;
    justify-content: center;
    padding-left: 5px;
    padding-right: 5px;
}
</style>
